import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Quest.module.css';
import '../styles/buttons.css';
import plaidService from '../services/plaidService';

export interface QuestData {
  id: string;
  title: string;
  description: string;
  reward: number;
  isStarted: boolean;
  timeRemaining?: number;
  progressPercentage: number;
  showTimer?: boolean;
  actionButtonText: string;
  actionButtonColor: string;
  onStart: () => void;
  onAction: () => void;
}

interface QuestProps {
  quest: QuestData;
  formatTime?: (seconds: number) => string;
  emergencyFundGoal?: string;
}

const Quest: React.FC<QuestProps> = ({ quest, formatTime, emergencyFundGoal }) => {
  const navigate = useNavigate();
  const {
    title,
    description,
    reward,
    isStarted,
    progressPercentage,
    actionButtonText,
    actionButtonColor,
    onStart,
    onAction
  } = quest;

  const completed = progressPercentage >= 100;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSavingsModal, setShowSavingsModal] = useState(false);
  const [checkingForTransfer, setCheckingForTransfer] = useState(false);
  const [showEatingOutModal, setShowEatingOutModal] = useState(false);
  const [checkingForEatingOut, setCheckingForEatingOut] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerInterval = useRef<NodeJS.Timeout | null>(null);

  // Helper: get localStorage key for quest timer
  const getTimerKey = useCallback(() => `quest_timer_${quest.id}`, [quest.id]);

  // Helper: format seconds as HH:MM:SS
  const formatCountdown = (seconds: number) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  // Handle timer completion for each quest
  const handleTimerComplete = useCallback(async () => {
    if (quest.id === 'eating-out') {
      setCheckingForEatingOut(true);
      try {
        const transactions = await plaidService.getPersistedTransactions();
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const found = transactions.some(tx => {
          const txDate = new Date(tx.date);
          if (tx.pending) return false;
          if (txDate < oneDayAgo) return false;
          const categories = (tx.category || []).map(c => c.toLowerCase());
          const desc = (tx.name + ' ' + (tx.merchant_name || '')).toLowerCase();
          const isRestaurant = categories.includes('restaurants') || categories.includes('restaurant') || desc.includes('restaurant') || desc.includes('cafe') || desc.includes('diner') || desc.includes('bar') || desc.includes('grill') || desc.includes('pizza') || desc.includes('burger') || desc.includes('bistro') || desc.includes('pub') || desc.includes('eatery');
          return isRestaurant;
        });
        if (!found) {
          onAction();
          setShowEatingOutModal(true);
        }
      } catch (err) {}
      setCheckingForEatingOut(false);
    } else if (quest.id === 'savings') {
      setCheckingForTransfer(true);
      try {
        const transactions = await plaidService.getPersistedTransactions();
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const found = transactions.some(tx => {
          const txDate = new Date(tx.date);
          if (tx.pending) return false;
          if (txDate < oneDayAgo) return false;
          const desc = (tx.name + ' ' + (tx.merchant_name || '')).toLowerCase();
          const isTransfer = desc.includes('transfer') || desc.includes('savings') || desc.includes('to savings');
          const amount = Math.abs(tx.amount);
          return isTransfer && amount >= 5;
        });
        if (found) {
          onAction();
          setShowSavingsModal(false);
        }
      } catch (err) {}
      setCheckingForTransfer(false);
    }
    localStorage.removeItem(getTimerKey());
  }, [quest.id, onAction, setShowEatingOutModal, setShowSavingsModal, setCheckingForEatingOut, setCheckingForTransfer, getTimerKey]);

  // On mount, check for existing timer
  useEffect(() => {
    if (quest.id === 'eating-out' || quest.id === 'savings') {
      const startTimeStr = localStorage.getItem(getTimerKey());
      if (startTimeStr) {
        const startTime = parseInt(startTimeStr, 10);
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        if (elapsed < 86400) {
          setTimer(86400 - elapsed);
          setTimerActive(true);
        } else {
          setTimer(0);
          setTimerActive(false);
        }
      }
    }
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
    };
  }, [quest.id, getTimerKey]);

  // Timer countdown effect
  useEffect(() => {
    if (timerActive && timer > 0) {
      timerInterval.current = setInterval(() => {
        setTimer(t => t - 1);
      }, 1000);
      return () => {
        if (timerInterval.current) clearInterval(timerInterval.current);
      };
    } else if (timerActive && timer <= 0) {
      setTimerActive(false);
      // Timer finished, check transactions for quest completion
      handleTimerComplete();
    }
  }, [timerActive, timer, handleTimerComplete]);

  // Debug logging for Financial Guru quest
  if (quest.id === 'financial-guru') {
    console.log('[Quest] Financial Guru quest data:', {
      id: quest.id,
      isStarted,
      progressPercentage,
      completed
    });
  }

  // Card style
  const cardClass = completed
    ? styles.questCardCompleted
    : styles.questCardInProgress;

  // Reward pill style
  const rewardClass = completed
    ? styles.rewardPillCompleted
    : styles.rewardPillInProgress;

  // Progress bar style
  const progressBarClass = completed
    ? styles.progressBarCompleted
    : styles.progressBarInProgress;

  // Status label
  let statusLabel = null;
  if (completed) {
    statusLabel = (
      <span className={styles.completedLabel}>
        Completed <span className={styles.check}>✔</span>
      </span>
    );
  } else if (isStarted) {
    statusLabel = (
      <span className={styles.inProgressLabel}>In Progress</span>
    );
  }

  // Custom start handler for Financial Guru quest
  const handleStart = async () => {
    if (quest.id === 'financial-guru') {
      onStart(); // Mark the quest as started
      navigate('/quests/financial-guru-article');
    } else if (quest.id === 'weekly-check-in') {
      onStart(); // Mark the quest as started
      navigate('/transactions');
    } else if (quest.id === 'savings') {
      // Start 24h timer
      const now = Date.now();
      localStorage.setItem(getTimerKey(), now.toString());
      setTimer(86400);
      setTimerActive(true);
      onStart();
    } else if (quest.id === 'eating-out') {
      // Start 24h timer
      const now = Date.now();
      localStorage.setItem(getTimerKey(), now.toString());
      setTimer(86400);
      setTimerActive(true);
      onStart();
    } else if (quest.id === 'emergency-fund') {
      onStart(); // Mark the quest as started
      navigate('/quests/emergency-fund-goal');
    } else {
      onStart();
    }
  };

  // Custom action handler for quests
  const handleAction = () => {
    if ((quest.id === 'savings' || quest.id === 'eating-out') && isStarted) {
      // User is giving up on the savings or eating-out quest
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      setTimer(0);
      setTimerActive(false);
      localStorage.removeItem(getTimerKey());
    }
    onAction();
  };

  return (
    <div className={cardClass}>
      <div className={styles.questHeader}>
        <div style={{ flex: 1 }}>
          <div 
            className={styles.questTitle}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>{title}</span>
            <span style={{ 
              fontSize: '0.8rem', 
              color: '#999',
              marginLeft: '0.5rem'
            }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          </div>
          {isExpanded && (
            <div className={styles.questDescription}>{description}</div>
          )}
          {isExpanded && quest.id === 'emergency-fund' && emergencyFundGoal && (
            <div style={{ color: '#4f46e5', fontWeight: 600, fontSize: '1.05rem', marginTop: 4 }}>
              Emergency Fund Goal: ${parseFloat(emergencyFundGoal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
        </div>
        <div className={rewardClass}>
          {reward} <span className={styles.heart}>❤️</span>
        </div>
      </div>
      
      {/* Expandable quest details */}
      {isExpanded && (
        <>
          <div className={styles.progressBarWrapper}>
            <div className={progressBarClass}>
              <div
                className={styles.progressFill}
                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
              />
            </div>
            <span className={styles.progressPercent}>{Math.round(progressPercentage)}%</span>
          </div>
          {/* Countdown timer for eating-out and savings quests */}
          {(timerActive || timer > 0) && (quest.id === 'eating-out' || quest.id === 'savings') && (
            <div style={{ textAlign: 'center', margin: '0.5rem 0', color: '#4f46e5', fontWeight: 600 }}>
              Time remaining: {formatCountdown(timer)}
            </div>
          )}
          <div className={styles.statusRow}>{statusLabel}</div>
        </>
      )}
      {/* Centered Start button */}
      {!isStarted && !completed && (
        <div className={styles.buttonRow}>
          <button
            onClick={handleStart}
            className={styles.startButton}
            disabled={checkingForTransfer || checkingForEatingOut}
          >
            {checkingForTransfer || checkingForEatingOut ? 'Checking...' : 'Start'}
          </button>
        </div>
      )}
      {/* Other action buttons (Give Up, Complete) */}
      {isStarted && !completed && (
        <div className={styles.buttonRow}>
          <button
            onClick={handleAction}
            className={styles.actionButton}
            style={{ backgroundColor: actionButtonColor }}
          >
            {actionButtonText}
          </button>
        </div>
      )}
      {completed && null}
      {/* Modal for savings quest */}
      {showSavingsModal && quest.id === 'savings' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(80, 0, 120, 0.18)',
            padding: '2rem 2.5rem',
            textAlign: 'center',
            maxWidth: '90vw',
            minWidth: 300
          }}>
            <h2 style={{ color: '#4f46e5', marginBottom: 12 }}>Heads up!</h2>
            <p style={{ color: '#333', marginBottom: 24, fontSize: '1.1rem' }}>
              This quest will show as complete when Plaid updates the transaction data.
            </p>
            <button
              style={{
                background: 'hsl(260, 80%, 74%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.8rem 2rem',
                fontSize: '1.1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onClick={() => {
                setShowSavingsModal(false);
                setTimeout(() => navigate('/games'), 500);
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {/* Modal for eating-out quest */}
      {showEatingOutModal && quest.id === 'eating-out' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(80, 0, 120, 0.18)',
            padding: '2rem 2.5rem',
            textAlign: 'center',
            maxWidth: '90vw',
            minWidth: 300
          }}>
            <h2 style={{ color: '#22c55e', marginBottom: 12 }}>Quest Complete!</h2>
            <p style={{ color: '#333', marginBottom: 24, fontSize: '1.1rem' }}>
              You did not eat out in the last 24 hours. Points awarded!
            </p>
            <button
              style={{
                background: 'hsl(260, 80%, 74%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.8rem 2rem',
                fontSize: '1.1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onClick={() => {
                setShowEatingOutModal(false);
                setTimeout(() => navigate('/games'), 500);
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quest; 